import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { cn } from '@/lib/utils'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'

/** Boxes stay square and legible and the row spreads to the field width, rather
 *  than the digits growing to fill it. No grouping separator: the emailed code is
 *  six plain digits, and a dash would imply a format it does not have. */
const SLOT = 'h-11 w-11 text-base sm:h-12 sm:w-12'

interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  length?: number
  disabled?: boolean
  autoFocus?: boolean
  /** Stretch the row to fill its container (stacked form fields). Turn off when the
   *  input sits inline beside a label, where it should take its natural width. */
  fullWidth?: boolean
  'aria-label'?: string
}

/** Segmented numeric code entry (paste-aware, one-time-code autofill). Shared by every
 *  flow that reads a Cognito verification code. */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled,
  autoFocus,
  fullWidth = true,
  'aria-label': ariaLabel = 'Verification code',
}: OtpInputProps) {
  return (
    <InputOTP
      maxLength={length}
      value={value}
      onChange={onChange}
      onComplete={onComplete}
      disabled={disabled}
      autoFocus={autoFocus}
      pattern={REGEXP_ONLY_DIGITS}
      inputMode="numeric"
      autoComplete="one-time-code"
      aria-label={ariaLabel}
      containerClassName={fullWidth ? 'w-full' : undefined}
    >
      <InputOTPGroup className={cn('gap-2', fullWidth && 'w-full justify-between')}>
        {Array.from({ length }, (_, i) => (
          <InputOTPSlot key={i} index={i} className={SLOT} />
        ))}
      </InputOTPGroup>
    </InputOTP>
  )
}
